// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds.
export function debounce(func, wait, immediate) {
	let timeout;
	return function (...args) {
		var later = () => {
			timeout = null;
			func.apply(this, args);
		};
		var callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
		if (callNow) func.apply(this, args);
	};
}

// same as debounce above, but allows an array of calls to be debounced separately based on a key
export function debouncePerKey(func, wait, immediate, indexArg = 1) {
	let timeOuts = [];
	return function (...args) {
		let index = args[indexArg];
		let timeout = timeOuts[index];
		let later = () => {
			timeOuts[index] = null;
			func.apply(this, args);
		};
		var callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeOuts[index] = setTimeout(later, wait);
		if (callNow) func.apply(this, args);
	}
}

// returns a function that, when invoked, will only be triggered no more than
// once every wait period.
export function limit(func, wait) {
	let timeout = null;
	// store the latest args, so that the newest values are used after the timeout
	let storedArgs = null;
	return function (...args) {
		storedArgs = args
		if (!timeout) {
			timeout = setTimeout(() => {
				timeout = null;
				func.apply(this, storedArgs);
			}, wait);
			func.apply(this, storedArgs);
		}
	}
}

export const limits = [
	{ bytes: 1000000000000, key: "TB" },
	{ bytes: 1000000000, key: "GB" },
	{ bytes: 1000000, key: "MB" },
	{ bytes: 1000, key: "KB" }
];

export function formatBytes(bytes) {
	for (let i = 0; i < limits.length; i++) {
		var limit = limits[i];
		if (bytes >= limit.bytes) {
			return (bytes / limit.bytes).toPrecision(3) + " " + limit.key;
		}
	}
	return bytes + " B";
}

// inspired by vuex internals for mapGetters
export function mapWatchers(watchers, handler, deep) {
	let result = {};

	normalizeMap(watchers).forEach(prop => {
		result[prop.key] = { handler, deep };
	});

	return result;
}
// see: https://github.com/vuejs/vuex/blob/dev/dist/vuex.js
function normalizeMap(map) {
	return Array.isArray(map)
		? map.map(key => { return { key, val: key } })
		: Object.keys(map).map(key => { return { key, val: map[key] } });
}

export function getsortedImages(unsortedImages, imagesOrder) {
	let sortedImages = [];
	imagesOrder.forEach(element => {
		let image = unsortedImages.find(a => a.flSrNo === element.flSrNo && a.imgSubType == element.imgSubType);
		sortedImages.push(image);
	});

	return sortedImages;
}

export function compareTimespans(a, b) {
	if (a.imgTimeStamp > b.imgTimeStamp) return 1;
	else if (a.imgTimeStamp < b.imgTimeStamp) return -1;
	return 0;
}

export function sortImagesByTimespan(images) {
	return images.sort(compareTimespans);
}

export function sortImagesByExamDate(images) {
	return images.sort(compareExamDate);
}

export function compareExamDate(a, b) {
	return new Date(b.exDate) - new Date(a.exDate);
}

export function getSameDateExams(exams, date){
	return exams.filter(e => e.dateDisplay === date);
}

export function getExamsWithUniqueDate(exams){
	return exams.reduce((unique, o) => {
		if(!unique.some(obj => obj.dateDisplay === o.dateDisplay)) {
			unique.push(o);
		}
		return unique;
	},[]);
}

// log a message with full logging
export function logDetailed(moduleName, functionName, message, ...args) {
	args = serializeArgs(args);
	message = ["axis5ui", moduleName, functionName, serializeArg(message), ...args]
		.filter(n => !!n)
		.join(" - ");
	console.log(message);
}

function serializeArgs(args){
	return args.map(serializeArg);
}

function serializeArg(arg){
	if (!arg) return String(arg);
	if (isFunction(arg)) return "function:" + arg.name;
	return JSON.stringify(arg);
}

function isFunction(obj){
	return !!(obj && obj.constructor && obj.call && obj.apply);
}

/*
// Function from http://detectmobilebrowsers.com/
export function isMobileOrTablet() 
{
	var isMobile = false;
	if(/(|android|ipad|playbook|silk|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent) 
			|| /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g|nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(navigator.userAgent.substr(0,4)))
			isMobile = true;
	return isMobile;
}
*/